import { useRef, useState, useEffect } from "react";
import { useAuth } from "@/hooks/use-auth";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { useToast } from "@/hooks/use-toast";
import { Loader2 } from "lucide-react";

interface LoginDropdownProps {
  onForgotPassword?: () => void;
  onSignup?: () => void;
  triggerButton?: React.ReactNode;
}

export function LoginDropdown({ onForgotPassword, onSignup, triggerButton }: LoginDropdownProps) {
  const emailRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();

  // Focar campo de email quando dropdown abrir
  useEffect(() => {
    if (open) {
      setTimeout(() => emailRef.current?.focus(), 100);
    }
  }, [open]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const email = emailRef.current?.value || '';
    const password = passwordRef.current?.value || '';
    
    if (!email || !password) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: "Preencha email e senha",
      });
      return;
    }
    
    setLoading(true);

    try {
      const { error } = await signIn(email, password);
      
      if (error) {
        toast({
          variant: "destructive",
          title: "Erro ao fazer login",
          description: error.message === "Invalid login credentials" 
            ? "Email ou senha incorretos" 
            : error.message,
        });
        return;
      }

      toast({
        title: "Login realizado!",
        description: "Redirecionando...",
      });
      
      setOpen(false);
      navigate("/");
      
      // Reset via refs
      if (emailRef.current) emailRef.current.value = '';
      if (passwordRef.current) passwordRef.current.value = '';
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Erro",
        description: error.message || "Ocorreu um erro ao fazer login",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        {triggerButton || <Button variant="outline">Login</Button>}
      </PopoverTrigger>
      <PopoverContent className="w-80 p-4" align="end">
        <form onSubmit={handleSubmit} className="space-y-4" autoComplete="on">
          <div className="space-y-2">
            <h3 className="font-semibold text-lg">Login - PortalONE Suite</h3>
          </div>

          <div className="space-y-2">
            <Label htmlFor="dropdown-email">Email</Label>
            <Input
              ref={emailRef}
              id="dropdown-email"
              name="email"
              type="email"
              placeholder="seu@email.com"
              defaultValue=""
              autoComplete="username"
              inputMode="email"
              required
              disabled={loading}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="dropdown-password">Senha</Label>
            <Input
              ref={passwordRef}
              id="dropdown-password"
              name="password"
              type="password"
              placeholder="••••••"
              defaultValue=""
              autoComplete="current-password"
              required
              disabled={loading}
            />
          </div>

          {onForgotPassword && (
            <Button
              type="button"
              variant="link"
              onClick={() => {
                console.log('🔍 Forgot password button clicked');
                setOpen(false);
                onForgotPassword();
              }}
              className="text-sm text-primary hover:underline p-0 h-auto font-normal"
            >
              Esqueceu sua senha?
            </Button>
          )}

          <Button type="submit" className="w-full" disabled={loading}>
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Entrando...
              </>
            ) : (
              "Entrar"
            )}
          </Button>

          {onSignup && (
            <div className="text-center text-sm text-muted-foreground">
              Não tem conta?{" "}
              <button
                type="button"
                onClick={() => {
                  setOpen(false);
                  onSignup();
                }}
                className="text-primary hover:underline"
              >
                Criar conta
              </button>
            </div>
          )}
        </form>
      </PopoverContent>
    </Popover>
  );
}
